import React, { useState, useEffect } from 'react';
import {
    PayPalButtons,
    usePayPalScriptReducer
} from "@paypal/react-paypal-js";
import axios from 'axios';
import { message } from 'react-message-popup';
import config from '../config/config.json';

interface PayPalCheckoutButtonProps {
    // The internal order ID created by Checkout.tsx or from OrderDetail.tsx
    internalOrderId: string;
    // The currency for the transaction
    currency: string;
    // The total amount for the transaction
    amount: number;
    // Callback function to execute after successful payment capture.
    onPaymentSuccess: (details: { internalOrderId: string; transactionId: string }) => void;
}

const PayPalCheckoutButton: React.FC<PayPalCheckoutButtonProps> = ({ internalOrderId, currency, amount, onPaymentSuccess }) => {

    const [{ isPending, isRejected }] = usePayPalScriptReducer();
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (isRejected) {
            console.error("🅿️ [FATAL] PayPal script failed to load.");
            message.error("Could not load the PayPal script. Please check your connection or ad-blocker and try again.");
        }
    }, [isRejected]);

    const createOrder = async (data: any, actions: any): Promise<string> => {
        console.log("🅿️ [CHECKPOINT] Creating PayPal transaction directly...");

        if (!amount || amount <= 0 || !currency) {
            const errorMsg = "Amount and currency are required to create a PayPal transaction.";
            console.error("🅿️ [FATAL]", errorMsg);
            message.error(errorMsg);
            throw new Error(errorMsg);
        }

        // This creates the order directly on PayPal's side without calling our backend.
        return actions.order.create({
            purchase_units: [
                {
                    amount: {
                        value: amount.toFixed(2), // Ensure it's a string with 2 decimal places
                        currency_code: currency,
                    },
                },
            ],
        });
    };

    const onApprove = async (data: any, actions: any): Promise<void> => {
        console.log("🅿️ [CHECKPOINT] Order approved by user. Capturing payment...", data);
        setIsProcessing(true); // Show a disabled state on the buttons

        try {
            const response = await axios.post(`${config.backend.url}/api/paypal/capture-payment`, {
                paypalOrderId: data.orderID, // This is PayPal's order ID
                internalOrderId: internalOrderId, // This is our internal order ID
            });

            console.log("🅿️ [SUCCESS] Payment captured successfully!", response.data);

            const capturedInternalOrderId = response.data.orderId;
            const transactionId = response.data.transactionId;

            if (!capturedInternalOrderId || !transactionId) {
                throw new Error("Backend did not return orderId and transactionId after capture.");
            }

            onPaymentSuccess({
                internalOrderId: capturedInternalOrderId.toString(),
                transactionId: transactionId,
            });
            
        } catch (error) {
            console.error("🅿️ [FATAL] Backend error capturing PayPal payment:", error);
            message.error("Your payment was approved, but we failed to finalize it. Please contact support.");
            setIsProcessing(false);
            throw error; // Re-throw to let PayPal SDK handle it
        }
    };

    const onError = (err: Record<string, unknown>) => {
        console.error("🅿️ [FATAL] PayPal button error:", err);
        message.error("An unexpected error occurred with PayPal. Please try again or contact support.");
    };

    // If the PayPal script is still loading, we can show a placeholder.
    if (isPending) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>Loading PayPal...</div>;
    }

    // If the script failed to load, we can show an error message.
    if (isRejected) {
        return <div style={{ color: 'red', textAlign: 'center' }}>Failed to load PayPal buttons. Please refresh the page.</div>;
    }

    return (
        <div className="paypal-button-container">
            <PayPalButtons
                style={{ layout: "vertical", label: "pay" }}
                disabled={isProcessing || amount <= 0}
                forceReRender={[amount, currency, internalOrderId]}
                createOrder={createOrder}
                onApprove={onApprove}
                onError={onError}
            />
        </div>
    );
};

export default PayPalCheckoutButton;